import Box from '@mui/material/Box';
import { mdiAccountRemove } from '@mdi/js';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client/react';

import { ITrainingSectionProps } from 'training/types';
import { PageTitle } from 'common/components/PageTitle';
import { getTrainingPermissions } from 'training/utils/permissions';
import { TrainingParticipantView } from 'training/components/TrainingParticipantView';
import { UserSelectDrawer } from 'user/components/UserSelector/UserSelectDrawer';
import {
    IContentTypeValue,
    ITrainingRole,
    ITrainingUserEdgeFragment,
    RemoveUsersFromTrainingDocument,
} from 'graphql/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import {
    IFilterFormItem,
    IPaginationProps,
    ITableSelectionProps,
    TFilterFormValue,
    TFilterFormValues,
} from 'common/types';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { useTrainingParticipantList } from 'training/hooks/useTrainingParticipantList';
import { useSelection } from 'common/components/Table';

import { AddUsersButton } from './AddUsersButton';

export interface ITableDataProps {
    paginationProps: IPaginationProps;
    searchProps: {
        filters: IFilterFormItem<TFilterFormValue>[];
        filterValues: TFilterFormValues;
        filterValuesForQuery: Record<string, unknown>;
        searchQueryParam: string;
        onSearch(searchValue: string): void;
        onFilterChange(values?: TFilterFormValues): void;
    };
    selectionProps: ITableSelectionProps<ITrainingUserEdgeFragment>;
}

interface IDeleteDialog {
    open: boolean;
    participantIds: string[];
}

export const Participants = ({ training }: ITrainingSectionProps) => {
    const [translate] = useTranslation();
    const client = useApolloClient();

    const { canManageParticipants = false } = getTrainingPermissions(training);
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const [deleteDialog, setDeleteDialog] = useState<IDeleteDialog>({
        open: false,
        participantIds: [],
    });

    const [
        deleteUsersFromTraining,
        { loading: deleteUsersFromTrainingLoading },
    ] = useMutation(RemoveUsersFromTrainingDocument);

    const { id } = training;

    const {
        participants,
        paginationSettings,
        loading: participantsLoading,
        filters,
        filterValues,
        filterValuesForQuery,
        setPage,
        onSearch,
        onFilterChange,
    } = useTrainingParticipantList(id);

    const { ...selectionProps } = useSelection(participants);
    const { selected, onSelectAll } = selectionProps;

    const resetSettings = () => {
        setPage?.(1);
        onSearch('');
        onFilterChange();

        if (!!selected.length) onSelectAll();
    };

    const handleRemoveParticipants = async () => {
        if (!participants?.length || !deleteDialog.participantIds.length) {
            return;
        }

        const selectedParticipantIds = deleteDialog.participantIds;

        try {
            const response = await deleteUsersFromTraining({
                variables: {
                    trainingId: id,
                    userIds: selectedParticipantIds,
                    roles: [ITrainingRole.Participant],
                },
                update(cache) {
                    cache.modify({
                        id: cache.identify(training),
                        fields: {
                            participants(_existingParticipants, { DELETE }) {
                                return DELETE;
                            },
                        },
                    });
                },
            });

            const { data } = response;
            const {
                removedIds,
                alreadyRemovedIds,
                failedIds,
                idsLinkedViaObject,
            } = data?.removeUsersFromTraining || {};

            if (!!idsLinkedViaObject?.length) {
                const couldNotRemoveParticipants = participants.filter(
                    (participantEdge) =>
                        idsLinkedViaObject.includes(participantEdge.node.id)
                );

                const couldNotRemoveParticipantNames =
                    couldNotRemoveParticipants
                        .map((participantEdge) => participantEdge.node.name)
                        .join(', ');

                displaySnackbar(
                    translate(
                        'trainingParticipants.participantsCouldNotRemove',
                        {
                            participants: couldNotRemoveParticipantNames,
                            count: couldNotRemoveParticipants.length,
                            interpolation: { escapeValue: false },
                        }
                    ),
                    {
                        variant: 'error',
                    }
                );
            }

            if (!!removedIds?.length) {
                displaySnackbar(
                    translate(
                        'trainingParticipants.participantsRemovedSuccess'
                    ),
                    {
                        variant: 'success',
                    }
                );

                client.cache.evict({ fieldName: 'paginatedUsers' });
                client.cache.gc();
            }

            if (!!alreadyRemovedIds?.length && participants?.length) {
                const alreadyRemovedParticipants = participants.filter(
                    (participantEdge) =>
                        alreadyRemovedIds.includes(participantEdge.node.id)
                );

                const alreadyRemovedParticipantNames =
                    alreadyRemovedParticipants
                        .map((participantEdge) => participantEdge.node.name)
                        .join(', ');

                displaySnackbar(
                    translate(
                        'trainingParticipants.participantsAlreadyRemoved',
                        {
                            trainers: alreadyRemovedParticipantNames,
                            count: alreadyRemovedParticipants.length,
                            interpolation: { escapeValue: false },
                        }
                    ),
                    {
                        variant: 'error',
                    }
                );
            }

            if (failedIds?.length && !!participants?.length) {
                const failedParticipants = participants.filter(
                    (participantEdge) =>
                        failedIds.includes(participantEdge.node.id)
                );

                const failedParticipantNames = failedParticipants
                    .map((participantEdge) => participantEdge.node.name)
                    .join(', ');

                displaySnackbar(
                    translate(
                        'trainingParticipants.participantsRemovedFailed',
                        {
                            trainers: failedParticipantNames,
                            count: failedParticipants.length,
                            interpolation: { escapeValue: false },
                        }
                    ),
                    {
                        variant: 'error',
                    }
                );
            }
        } catch (error) {
            showApolloError(error);
        }

        resetSettings();
        closeDeleteDialog();
    };

    const closeDeleteDialog = () => {
        setDeleteDialog({ open: false, participantIds: [] });
    };

    const tableActions = canManageParticipants
        ? [
              {
                  tooltipTitle: translate(
                      'trainingParticipants.deleteFromTraining'
                  ),
                  iconPath: mdiAccountRemove,
                  onClick: (selectedParticipantIds: string[]) => {
                      setDeleteDialog?.({
                          open: true,
                          participantIds: selectedParticipantIds,
                      });
                  },
              },
          ]
        : [];

    const tableDataProps: ITableDataProps = {
        paginationProps: { paginationSettings, setPage },
        searchProps: {
            filters,
            filterValues,
            filterValuesForQuery,
            onSearch,
            onFilterChange,
            searchQueryParam: filterValues.q,
        },
        selectionProps,
    };

    return (
        <Box>
            <PageTitle>
                {`${translate('participants')} - ${training.title}`}
            </PageTitle>

            {canManageParticipants ? (
                <AddUsersButton
                    training={training}
                    onParticipantsAdded={resetSettings}
                >
                    {({
                        onAddUsers,
                        loadingAddUsers,
                        isSelectorOpen,
                        onSelectorOpenChange,
                        selectDrawerFooterAppend,
                    }) => {
                        const loading = participantsLoading || loadingAddUsers;

                        return (
                            <Box sx={{ mt: 2 }}>
                                <TrainingParticipantView
                                    isEditable={canManageParticipants}
                                    loading={loading}
                                    participants={participants}
                                    tableActions={tableActions}
                                    tableDataProps={tableDataProps}
                                    training={training}
                                />

                                <UserSelectDrawer
                                    footerAppend={selectDrawerFooterAppend}
                                    loading={loading}
                                    open={isSelectorOpen}
                                    selectorQueryVariables={{
                                        contentType: IContentTypeValue.Training,
                                        objectId: id,
                                    }}
                                    onAddUsers={onAddUsers}
                                    onDrawerOpen={onSelectorOpenChange}
                                />
                            </Box>
                        );
                    }}
                </AddUsersButton>
            ) : (
                <TrainingParticipantView
                    isEditable={false}
                    loading={participantsLoading}
                    participants={participants}
                    tableActions={tableActions}
                    tableDataProps={tableDataProps}
                    training={training}
                />
            )}

            <ConfirmDialog
                color="error"
                confirmText={translate('delete')}
                loading={deleteUsersFromTrainingLoading}
                open={deleteDialog.open}
                title={translate(
                    'trainingParticipants.deleteTrainingParticipantMessage.title'
                )}
                onCancel={closeDeleteDialog}
                onClose={closeDeleteDialog}
                onConfirm={handleRemoveParticipants}
            >
                {translate(
                    'trainingParticipants.deleteTrainingParticipantMessage.text'
                )}
            </ConfirmDialog>
        </Box>
    );
};
